import React, { useMemo } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  AE_ICON_SIZE,
  AE_MUTED,
} from '@/components/assessment-engine/layout/aeTheme';
import type { AeTemplateDetail } from '@/types/assessmentEngine';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import { SectionBlock } from './SectionBlock';
import { internalSectionCode } from './numberingUtils';
import { BuilderIconButton } from './BuilderIconButton';
import { refreshAndPersistDimensions } from './dimensionSync';
import { DEMOGRAPHICS_SECTION_CODE, templateConfigFromDetail } from './templateConfigTypes';
import { TemplateBuilderDnd } from './TemplateBuilderDnd';
import { countTemplateQuestions } from './templateStructureStats';
import { topLevelSectionDndIds } from './builderDnd';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

interface TemplateVisualBuilderProps {
  template: AeTemplateDetail;
  /** When true, survey structure (sections/questions) is locked. Name/description are on Setup tab. */
  structureReadOnly?: boolean;
  /** @deprecated Use structureReadOnly */
  readOnly?: boolean;
  onReload: () => void;
}

/** WYSIWYG survey questions — sections, demographics preview (conditional logic is its own tab). */
export const TemplateVisualBuilder: React.FC<TemplateVisualBuilderProps> = ({
  template,
  structureReadOnly: structureReadOnlyProp,
  readOnly,
  onReload,
}) => {
  const structureReadOnly = structureReadOnlyProp ?? readOnly ?? false;
  const { t } = useLanguage();

  const templateConfig = templateConfigFromDetail(template);
  const scalePresets = templateConfig.scale_presets ?? [];
  const questionStats = useMemo(() => countTemplateQuestions(template.sections), [template.sections]);
  const demographicsSection = template.sections.find((s) => s.code === DEMOGRAPHICS_SECTION_CODE);
  const contentSections = template.sections.filter((s) => s.code !== DEMOGRAPHICS_SECTION_CODE);
  const demoAtEnd = templateConfig.demographics?.placement === 'end';

  const topLevelContentSections = template.sections.filter(
    (s) => s.code !== DEMOGRAPHICS_SECTION_CODE && !s.parent_section_id
  );

  const addSection = async () => {
    const label = t('assessmentEngine.builder.newDimensionDefault');
    const idx = topLevelContentSections.length;
    await assessmentEngineService.createSection(template.id, {
      code: internalSectionCode(idx),
      name: label,
      sort_order: idx,
    });
    await refreshAndPersistDimensions(template.id);
    onReload();
  };

  return (
    <TemplateBuilderDnd
      template={template}
      templateId={template.id}
      readOnly={structureReadOnly}
      onReload={onReload}
    >
    <div className="space-y-8">
      <p className={AE_MUTED}>
        {questionStats.conditional > 0
          ? t('assessmentEngine.builder.templateQuestionTotalWithConditional', {
              total: String(questionStats.total),
              conditional: String(questionStats.conditional),
            })
          : t('assessmentEngine.builder.templateQuestionTotal', {
              total: String(questionStats.total),
            })}
      </p>

      {!demoAtEnd && demographicsSection && (
        <SectionBlock
          templateId={template.id}
          template={template}
          section={demographicsSection}
          sectionNumber={0}
          readOnly
          scalePresets={scalePresets}
          onReload={onReload}
        />
      )}

      <div className="space-y-10">
        <SortableContext
          items={topLevelSectionDndIds(template)}
          strategy={verticalListSortingStrategy}
        >
        {contentSections.map((section, idx) => (
            <SectionBlock
              key={section.id}
              templateId={template.id}
              template={template}
              section={section}
              sectionNumber={idx + 1}
              readOnly={structureReadOnly}
              scalePresets={scalePresets}
              onReload={onReload}
            />
          ))}
        </SortableContext>
      </div>

      {demoAtEnd && demographicsSection && (
        <SectionBlock
          templateId={template.id}
          template={template}
          section={demographicsSection}
          sectionNumber={0}
          readOnly
          scalePresets={scalePresets}
          onReload={onReload}
        />
      )}

      {!structureReadOnly && (
        <BuilderIconButton
          label={t('assessmentEngine.builder.addDimension')}
          onClick={() => void addSection()}
          variant="primary"
        >
          <PlusIcon className={AE_ICON_SIZE} aria-hidden />
        </BuilderIconButton>
      )}
    </div>
    </TemplateBuilderDnd>
  );
};
